// src/lib/api.js
// Single point of contact between React and the backend.
// Adds Firebase ID token auth to every protected backend request.

import { auth } from "../firebase";

const BASE = import.meta.env.VITE_API_BASE_URL?.trim();

if (!BASE) {
  throw new Error("Missing VITE_API_BASE_URL");
}

async function getAuthHeaders() {
  const user = auth.currentUser;

  if (!user) {
    throw new Error("Not authenticated. Please sign in.");
  }

  const token = await user.getIdToken();

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

async function throwBackendError(res, fallbackMessage) {
  let detail = "";

  try {
    const contentType = res.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const json = await res.json();
      detail =
        json?.detail || json?.message || json?.error || JSON.stringify(json);
    } else {
      detail = await res.text();
    }
  } catch {
    detail = "";
  }

  const mapped =
    res.status === 401
      ? "Authentication failed. Please sign in again."
      : res.status === 403
        ? "Your account is authenticated but not authorised for PAT access."
        : res.status === 413
          ? "Request too large."
          : res.status === 429
            ? "Too many requests. Please wait and try again."
            : res.status === 503
              ? "Service temporarily unavailable."
              : fallbackMessage;

  console.error("PAT backend failed", {
    status: res.status,
    statusText: res.statusText,
    detail,
  });

  throw new Error(
    `${mapped} (${res.status} ${res.statusText})${detail ? `: ${detail}` : ""}`,
  );
}

/**
 * Call backend /api/pat/analyze — returns raw backend JSON.
 */
export async function analyze(message, overrides = {}) {
  if (!message || typeof message !== "string") {
    throw new Error("analyze() requires a non-empty message string");
  }

  if (overrides && typeof overrides !== "object") {
    throw new Error("analyze() overrides must be an object");
  }

  const headers = await getAuthHeaders();

  const res = await fetch(`${BASE}/api/pat/analyze`, {
    method: "POST",
    headers,
    body: JSON.stringify({ message, ...overrides }),
  });

  if (!res.ok) {
    await throwBackendError(res, "PAT backend request failed.");
  }
  return await res.json();
}

export async function confirmPreCausal(payload) {
  const headers = await getAuthHeaders();

  const res = await fetch(`${BASE}/api/pat/pre_causal/confirm`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    await throwBackendError(res, "Pre-causal confirmation failed.");
  }

  return await res.json();
}

/**
 * Normalise backend response so UI receives PAT fields at top level.
 * CRITICAL: clears stale pre_causal_bundle unless explicitly required.
 */
export function parseApiResponse(data) {
  if (!data || typeof data !== "object") {
    return { raw: data };
  }

  const rawPipe =
    data.pipeline_result && typeof data.pipeline_result === "object"
      ? data.pipeline_result
      : null;

  let pipeline_result = rawPipe ? { ...rawPipe } : null;

  if (
    pipeline_result &&
    pipeline_result.engine_phase !== "pre_causal_structuring"
  ) {
    pipeline_result.pre_causal_bundle = null;
  }

  const pat_variables =
    data.pat_variables ||
    (pipeline_result ? pipeline_result.pat_variables : null) ||
    null;

  const pat_tensor =
    data.pat_tensor ||
    (pipeline_result ? pipeline_result.pat_tensor : null) ||
    null;

  const pat_causal_prompt =
    data.pat_causal_prompt ||
    (pipeline_result ? pipeline_result.pat_causal_prompt : null) ||
    null;

  const pat_claim_frame =
    data.pat_claim_frame ||
    (pipeline_result ? pipeline_result.pat_claim_frame : null) ||
    null;

  const pat_evidence =
    data.pat_evidence ||
    (pipeline_result ? pipeline_result.pat_evidence : null) ||
    null;

  const pat_evidence_links =
    data.pat_evidence_links ||
    (pipeline_result ? pipeline_result.pat_evidence_links : null) ||
    null;

  const pat_causal_model =
    data.pat_causal_model ||
    (pipeline_result ? pipeline_result.pat_causal_model : null) ||
    null;

  const pat_confounders =
    data.pat_confounders ||
    (pipeline_result ? pipeline_result.pat_confounders : null) ||
    null;

  const pat_do =
    data.pat_do || (pipeline_result ? pipeline_result.pat_do : null) || null;

  const pat_classic_bayes =
    data.pat_classic_bayes ||
    (pipeline_result ? pipeline_result.pat_classic_bayes : null) ||
    null;

  const pat_bcsm =
    data.pat_bcsm ||
    (pipeline_result ? pipeline_result.pat_bcsm : null) ||
    null;

  const pat_distillation =
    data.pat_distillation ||
    (pipeline_result ? pipeline_result.pat_distillation : null) ||
    null;

  const selected_outcome =
    (pipeline_result ? pipeline_result.selected_outcome : null) ??
    data.selected_outcome ??
    null;

  const engine_phase =
    (pipeline_result ? pipeline_result.engine_phase : null) ??
    data.engine_phase ??
    null;

  return {
    raw: data,

    engine_mode:
      data.engine_mode ||
      (pipeline_result ? pipeline_result.engine_mode : null) ||
      "PAT",

    engine_phase,
    selected_outcome,
    pat_distillation,

    pat_scope:
      data.pat_scope ||
      (pipeline_result ? pipeline_result.pat_scope : null) ||
      null,

    pat_scope_status:
      data.pat_scope_status ||
      (pipeline_result ? pipeline_result.pat_scope_status : null) ||
      null,

    pat_scope_message:
      data.pat_scope_message ||
      (pipeline_result ? pipeline_result.pat_scope_message : null) ||
      null,

    pat_variables,
    pat_tensor,
    pat_claim_frame,
    pat_evidence,
    pat_evidence_links,
    pat_causal_model,
    pat_confounders,
    pat_do,
    pat_classic_bayes,
    pat_bcsm,
    pat_causal_prompt,
    pipeline_result,
  };
}

/**
 * Submit user-confirmed DAG and return resumed PAT pipeline
 * via /api/pat/dag/confirm.
 */
export async function confirmDag(payload) {
  const headers = await getAuthHeaders();

  const res = await fetch(`${BASE}/api/pat/dag/confirm`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    await throwBackendError(res, "DAG confirmation failed.");
  }
  return await res.json();
}
